import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";
import { Link } from "@arctheme-components/elements/Link/Link";
import {Button} from "@src/stories/elements/Button/Button";
import { useHubSpotForm } from "@src/hooks/useHubSpotForm";

export const SideHeaderMenu = ({ settings }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isDarkBackground, setIsDarkBackground] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const iconRef = useRef(null);
    const menuButtonRef = useRef(null);

    const isMobile = useMobileBreakpoint('1024px');
    const menuLinks = settings.settings.links[0].links;

    // Initialize HubSpot form for contact
    const { openForm } = useHubSpotForm({
        portalId: settings.settings.globals.hubspot.portal_id,
        formId: settings.settings.globals.hubspot.form_id,
        region: settings.settings.globals.hubspot.form_region,
    });

    const checkBackground = () => {
        if (iconRef.current) {
            // Get the icon position
            const rect = iconRef.current.getBoundingClientRect();
            
            // Find all elements at this position
            const elements = document.elementsFromPoint(
                rect.left + rect.width / 2,
                rect.top + rect.height / 2
            );
            
            // Filter out the icon itself and its ancestors
            let targetElement;
            let iconElement = iconRef.current;
           

            for (const element of elements) {
                // Skip the icon itself and its parent elements
                let isIconOrParent = false;
                let parent = iconElement;
                
                while (parent) {
                    if (parent === element) {
                        isIconOrParent = true;
                        break;
                    }
                    parent = parent.parentElement;
                }
                
                if (!isIconOrParent) {
                    // Check if the element has a non-transparent background color
                    const computedStyle = window.getComputedStyle(element);
                    const bgColor = computedStyle.backgroundColor;
                    
                    // Only set as target if it has a non-transparent background
                    if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
                        // Check if it's actually a color with RGB values
                        const rgbMatch = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
                        
                        if (rgbMatch) {
                            // If it has an alpha channel (rgba), check if it's not fully transparent
                            if (rgbMatch.length > 4) {
                                const alpha = parseFloat(rgbMatch[4]);
                                if (alpha > 0) {
                                    targetElement = element;
                                    break;
                                }
                            } else {
                                // RGB color without alpha is always visible
                                targetElement = element;
                                break;
                            }
                        }
                    }
                }
            }
            
            if (targetElement) {
                // Get background color of the element behind the icon
                // console.log(targetElement);
                const bgColor = window.getComputedStyle(targetElement).backgroundColor;
                
                // Parse RGB values
                const rgbMatch = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
                
                if (rgbMatch) {
                    const r = parseInt(rgbMatch[1], 10);
                    const g = parseInt(rgbMatch[2], 10);
                    const b = parseInt(rgbMatch[3], 10);
                    
                    // Calculate brightness - standard formula for perceived brightness
                    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
                    
                    // Set dark background state if brightness is below threshold
                    setIsDarkBackground(brightness < 128);
                } else {
                    // Try to get background color from computed style
                    const computedStyle = window.getComputedStyle(targetElement);
                    const bgColorValue = computedStyle.backgroundColor;
                    
                    // If it's transparent, check if it has a background image
                    if (bgColorValue === 'rgba(0, 0, 0, 0)' || bgColorValue === 'transparent') {
                        const bgImage = computedStyle.backgroundImage;
                        if (bgImage && bgImage !== 'none') {
                            // Background image exists - assume it might be dark
                            setIsDarkBackground(true);
                        } else {
                            // Default to light background
                            setIsDarkBackground(false);
                        }
                    }
                }
            } else {
                // No element found, default to light background
                setIsDarkBackground(false);
            }
        }
    };

    const checkHeaderVisibility = () => {
        // Find the header actions element
        const headerActions = document.querySelector('.header-toolbar');
        
        if (headerActions) {
            const rect = headerActions.getBoundingClientRect();
            // Consider header scrolled past if its bottom is above the viewport
            const isScrolledPast = rect.bottom < 0;
            setIsVisible(isScrolledPast);
        }
    };

    const onOpenMenu = (e) => {
        // Force menu to open from header actions click
        if (e.detail.message === 'header-menu') {
            setIsVisible(true);
            setIsOpen(true);
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    checkBackground();
                }
            });
        }, { threshold: 0.1 });

        if (iconRef.current) {
            observer.observe(iconRef.current);
        }

        window.addEventListener('scroll', checkHeaderVisibility);
        window.addEventListener('scroll', checkBackground);
        window.addEventListener('onOpenMenu', onOpenMenu);
        
        // Initial check
        checkHeaderVisibility();
        
        return () => {
            if (iconRef.current) {
                observer.unobserve(iconRef.current);
            }
            window.removeEventListener('scroll', checkBackground);
            window.removeEventListener('scroll', checkHeaderVisibility);
            window.removeEventListener('onOpenMenu', onOpenMenu);
        };
    }, []);

    const handleContactClick = () => {
        openForm();
    };

    return (
        <>
            {/* Container positioning adjusted for both desktop and mobile */}
            <div
                ref={menuButtonRef}
                className={`fixed z-50 transition-all duration-500 ${
                    isMobile 
                        ? `mobile-menu-button top-0 right-0` 
                        : `desktop-menu-button top-1/2 -translate-y-1/2 right-0 -mr-[220px] rounded-full ${isVisible || isOpen ? 'translate-x-0' : 'translate-x-full'}` 
                } outside-ring`}
                style={{
                    width: `${isMobile ? '64px' : '300px'}`,
                    height: `${isMobile ? '64px' : '170px'}`,
                    border: `${isMobile ? 'none' : '2px solid'}`,
                    borderColor: `${isOpen ? '#D6CFF8' : '#D6CFF8'}`, 
                    backgroundColor: `${isOpen ? '#FBF8F4' : 'rgba(38, 38, 38, 0.60)'}`,
                    backdropFilter: 'blur(6.9px)',
                    opacity: `${isOpen ? '1' : '1'}`,
                }}
            >
                {/* Button positioning adjusted for both views */}
                <button
                    className={`absolute ${
                        isMobile 
                            ? 'top-[55%] -translate-y-1/2 left-[50%] -translate-x-1/2 z-[1]' // Mobile: centered in circle
                            : 'top-1/2 -translate-y-1/2 left-[5%]' // Desktop: left side of circle (visible part)
                    } p-3 text-[32px] ${isOpen ? "text-white" : "text-black"}`}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {!isOpen && (<span ref={iconRef} className={`open-icon icon-menu pointer-events-none ${isMobile ? 'text-black' : 'text-[#F1E8DE]'}`}></span>)}
                    {isOpen && (<span className={`open-icon icon-X text-black`}></span>)}
                </button>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        className="fixed inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-40 m-2 lg:m-[18px] rounded-[4px]"
                        style={{
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)'
                        }}
                    >
                        <div className="menu">
                            <div className="flex flex-col justify-between w-full h-full">
                                <div className={`flex p-10 w-full ${isMobile ? 'justify-center items-center' : 'grid grid-cols-3 items-center'}`}>
                                    <div className="w-[150px] ">
                                        <img
                                            src="https://mindarc.myshopify.com/cdn/shop/files/MindArc_Main_Logo.png?v=1735509074&width=1200&width=640&format=webp"
                                            alt="logo"
                                            className={`w-full h-full object-cover rounded-full invert `}
                                        />
                                    </div>
                                    <div className={`h4 menu-title text-white text-center ${isMobile ? '!hidden' : ''}`}>
                                        Menu
                                    </div>

                                    <Button 
                                        onClick={handleContactClick} 
                                        className={`button button-secondary w-fit justify-self-end ${isMobile ? '!hidden' : ''}`} 
                                        style="link" 
                                        icon="right-arrow-alt" 
                                        label={`Contact us`} 
                                        reverse={true} 
                                    />

                                </div>

                                <nav className="h3 text-center text-white space-y-6">
                                    {menuLinks.map((link, index) => (
                                        <a key={index} href={link.url} className="block hover:text-gray-400">{link.title}</a>
                                    ))}
                                </nav>

                                <Button 
                                    onClick={handleContactClick} 
                                    className={`button button-secondary ${isMobile ? 'w-fit mx-auto' : 'lg:hidden'}`} 
                                    style="link" 
                                    icon="right-arrow-alt" 
                                    label={`Contact us`} 
                                    reverse={true} 
                                />

                                {!isMobile ? (
                                    <div className="text-white b_large_light flex space-x-4 justify-center mb-[30px]">
                                        <a href="https://www.instagram.com/mindarcmade" className="hover:text-gray-400">Instagram</a>
                                        <span>|</span>
                                        <a href="https://www.facebook.com/MindArcGroup/" className="hover:text-gray-400">Facebook</a>
                                        <span>|</span>
                                        <a href="https://www.linkedin.com/company/mindarc-group/" className="hover:text-gray-400">LinkedIn</a>
                                    </div>
                                ) : (
                                    <div className="text-white text-[32px] flex space-x-8 justify-center mb-[30px]">
                                        <a href="https://www.instagram.com/mindarcmade" className="hover:text-gray-400 icon-Instagram"></a>
                                        <a href="https://www.facebook.com/MindArcGroup/" className="hover:text-gray-400 icon-Facebook"></a>
                                        <a href="https://www.linkedin.com/company/mindarc-group/" className="hover:text-gray-400 text-[28px] icon-linkedin"></a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default SideHeaderMenu;